import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AvatarUpload } from '@/components/ui/avatar-upload'
import { useUser, useIsLoading } from '@/app/store/auth'
import { useUpdateUser } from '@/features/users/hooks'
import { useMe } from '@/features/auth/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { User, Save } from 'lucide-react'

interface SimpleEditProfileDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function SimpleEditProfileDialog({ open, onOpenChange }: SimpleEditProfileDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const user = useUser()
    const isLoading = useIsLoading()
    const updateUser = useUpdateUser()
    const meQuery = useMe()

    // Simple state management instead of complex form
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
    const [avatarColor, setAvatarColor] = useState<string | null>(null)
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)

    // Reset form when dialog opens
    useEffect(() => {
        if (open && user && !isLoading && user.id && user.name) {
            setName(user.name || '')
            setEmail(user.email || '')
            setAvatarUrl(user.avatarUrl || null)
            setAvatarColor(user.avatarColor || null)
        }
    }, [open, isLoading]) // Removed user dependencies to prevent resetting avatarColor

    const handleAvatarChange = async (file: File | null) => {
        if (file) {
            setIsUploadingAvatar(true)
            try {
                const formData = new FormData()
                formData.append('avatar', file)
                formData.append('subdirectory', 'avatars')

                const response = await fetch('/api/upload', {
                    method: 'POST',
                    body: formData,
                    credentials: 'include'
                })

                if (response.ok) {
                    const result = await response.json()
                    setAvatarUrl(result.image.url)
                    setAvatarColor(null) // Clear color when image is uploaded
                } else {
                    alert('Failed to upload avatar')
                }
            } catch (error) {
                alert('Error uploading avatar')
            } finally {
                setIsUploadingAvatar(false)
            }
        } else {
            // Remove avatar - clear both URL and color
            setAvatarUrl(null)
            setAvatarColor(null)
        }
    }

    const handleColorChange = (color: string) => {
        console.log('handleColorChange called with color:', color)
        setAvatarColor(color)
        setAvatarUrl(null) // Clear avatar URL when color is selected
        console.log('Avatar color set to:', color)
    }

    const handleSubmit = async () => {
        if (!user) return

        try {
            await updateUser.mutateAsync({
                id: user.id,
                data: {
                    name,
                    email,
                    avatarUrl: avatarUrl || null,
                    avatarColor: avatarColor || null,
                    // Include existing roles to prevent them from being cleared
                    roles: user.roles?.map(role => role.id) || [],
                    isActive: user.isActive,
                    mfaRequired: user.mfa?.totpEnabled || user.mfa?.webauthnEnabled || false,
                    mfaMethods: [
                        ...(user.mfa?.totpEnabled ? ['totp' as const] : []),
                        ...(user.mfa?.webauthnEnabled ? ['webauthn' as const] : [])
                    ],
                },
            })

            await meQuery.refetch()

            toast({
                title: 'Profile Updated',
                description: 'Your profile has been updated successfully.',
            })

            onOpenChange(false)
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to update profile. Please try again.',
                variant: 'destructive',
            })
        }
    }

    if (!user || isLoading || !user.id || !user.name) {
        return null
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        {t('profile.editProfile')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('profile.editProfileDesc')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Profile Picture */}
                    <div className="space-y-2">
                        <Label>{t('profile.profilePicture')}</Label>
                        <AvatarUpload
                            name={name || user.name || 'User'}
                            avatarUrl={avatarUrl}
                            avatarColor={avatarColor}
                            userId={user.id}
                            onAvatarChange={handleAvatarChange}
                            onColorChange={handleColorChange}
                            disabled={isUploadingAvatar}
                        />
                    </div>

                    {/* Name */}
                    <div className="space-y-2">
                        <Label htmlFor="name">{t('profile.name')}</Label>
                        <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('profile.namePlaceholder')}
                        />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                        <Label htmlFor="email">{t('profile.email')}</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('profile.emailPlaceholder')}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        {t('profile.cancel')}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={updateUser.isPending}
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {updateUser.isPending ? t('common.saving') : t('profile.saveChanges')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
